import {useState, useRef, useEffect} from 'react'
import {useFormik} from 'formik'
import * as Yup from 'yup'
import clsx from 'clsx'
import { useIntl } from 'react-intl'

import { MarkdownWithLinks } from '../../../components/MarkdownWithLinks'

import {Modal} from '../../../components/Modal'

interface Props {
  show: boolean
  handleClose: () => void
  handleSubmit: (verficationCode: string) => void
  handleResend: () => void
}

const schema = Yup.object().shape({
  verification_code: Yup.string().required('Required'),
});

const EmailVerificationModal : React.FC<Props> = ({show, handleClose, handleSubmit, handleResend}) => {

  const [handling, setHandling] = useState(false)

  const verificationCodeInput = useRef<HTMLInputElement | null>(null)

  const intl = useIntl()

  useEffect(() => {
    if (verificationCodeInput.current) {
      verificationCodeInput.current.focus()
    }
  }, [])

  const formik = useFormik({
    initialValues: {'verification_code': ''},
    validationSchema: schema,
    onSubmit: async (values) => {
      setHandling(true)
      try{
        await handleSubmit(values.verification_code)
      } finally {
        setHandling(false)
      }
    }
  })

  const handleResend_ = async () => {
    setHandling(true)
    try{
      await handleResend()
    } finally {
      setHandling(false)
    }
  }


  return <Modal show={show} handleClose={handleClose} dialogClassName="" title={intl.formatMessage({id: 'api_access_request.verification.title'})}>
    <form
      className='form fv-plugins-bootstrap5 fv-plugins-framework'
      onSubmit={formik.handleSubmit}
      noValidate
    >
      <div className="fs-6 mb-5">
        <MarkdownWithLinks>{intl.formatMessage({id: 'api_access_request.verification.description'})}</MarkdownWithLinks>
      </div>
      <div className='fv-row form-floating'>
        <input
          placeholder={intl.formatMessage({id: `api_access_request.verification_code`})}
          type='password'
          autoComplete='off'
          ref={verificationCodeInput}
          {...formik.getFieldProps('verification_code')}
          className={clsx(
            'form-control bg-transparent h-60px border border-gray-400',
            {
              'is-invalid': formik.touched.verification_code && formik.errors.verification_code,
            },
            {
              'is-valid': formik.touched.verification_code && !formik.errors.verification_code,
            }
          )}
        />
        <label className='form-label fs-6 text-gray-600'>{intl.formatMessage({id: `api_access_request.verification.label`})}</label>
      </div>
      <div className="d-flex justify-content-between gap-3 fs-base fw-semibold mb-4">
        <button 
          type="button" 
          className="btn btn-link link-primary" 
          disabled={handling} 
          onClick={handleResend_}
        >
          {intl.formatMessage({id: `api_access_request.verification.resend`})}
        </button>
      </div>
      <div className="text-center">
        <button
          type='submit'
          className="btn btn-primary"
          disabled={handling || !formik.isValid}
        >
          {!handling && <span className='indicator-label'>{intl.formatMessage({id: 'api_access_request.verify_and_submit'})}</span>}
          {handling && (
            <span className='indicator-progress' style={{display: 'block'}}>
              {intl.formatMessage({id: `general.please_wait`})}
              <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
            </span>
          )}
        </button>
        
        <button type="button" className="btn btn-light ms-3" onClick={handleClose}>
          {intl.formatMessage({id: `general.close`})}
        </button>
      </div>
    </form>
  </Modal>
}

export {EmailVerificationModal}